// src/components/pages/TuitionProfile.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MapPin,
  Banknote,
  CalendarDays,
  Building,
  ArrowLeft,
  BookOpen,
  User,
} from "lucide-react";
import { UserContext } from "../context/UserContext";
import Footer from "../Footer";

const API_BASE = "https://tn.server.goatteachers.com/api";

const normalizeBDPhone = (raw) => {
  if (!raw) return "";
  let d = String(raw).replace(/\D/g, "");

  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("880")) return d;
  if (d.startsWith("88")) return d;
  if (d.startsWith("0")) return "880" + d.slice(1);
  if (d.startsWith("1")) return "880" + d;

  return d;
};

const buildTutorWhatsappText = ({ tutor, tutorProfileUrl, tuitionCode }) => {
  return `আসসালামু আলাইকুম,

Tutor Network থেকে আমাকে ${tuitionCode || "-"} শিক্ষার্থীর টিউশনের জন্য Assign করা হয়েছে।

আমি এই টিউশনের জন্য আগ্রহী। আমার তথ্য নিচে দেওয়া হলো:

নাম: ${tutor?.name || "-"}
শিক্ষাগত যোগ্যতা:
${tutor?.degree || "-"}, ${tutor?.field_of_study || "-"}, ${tutor?.institute || "-"}

Tutor Profile:
${tutorProfileUrl || "-"}

আশা করি শিক্ষার্থীর প্রয়োজন অনুযায়ী ভালোভাবে সহযোগিতা করতে পারব।

ধন্যবাদ।
${tutor?.name || "-"}`;
};

const buildTutorCallLink = (phone) => {
  if (!phone) return "";

  const normalized = normalizeBDPhone(phone);

  return normalized ? `tel:+${normalized}` : "";
};

function buildSalaryText(raw) {
  const minSalary = raw.min_salary ?? null;
  const maxSalary = raw.max_salary ?? null;

  if (minSalary && maxSalary && minSalary !== maxSalary) {
    return `${minSalary} - ${maxSalary} ৳`;
  }

  if (minSalary && !maxSalary) {
    return `${minSalary} ৳ বা বেশি`;
  }

  if (!minSalary && maxSalary) {
    return `সর্বোচ্চ ${maxSalary} ৳`;
  }

  if (minSalary && maxSalary && minSalary === maxSalary) {
    return `${minSalary} ৳`;
  }

  return "Negotiable";
}

function mapTuition(raw) {
  const subjects =
    raw.TuitionPostSubjects?.map((ts) => ts.StudentSubject?.name).filter(
      Boolean,
    ) || [];

  const areas =
    raw.TuitionPostAreas?.map((ta) => ta.Area?.name).filter(Boolean) || [];

  const division = raw.Division?.name || "";
  const district = raw.District?.name || "";

  const locationParts = [areas.join(", "), district, division].filter(Boolean);

  return {
    id: raw.id,
    studentName: raw.student_name || "Unnamed Student",
    medium: raw.StudentMedium?.name || "Any medium",
    level: raw.StudentLevel?.name || "Level not set",
    subjects,
    location:
      locationParts.length > 0 ? locationParts.join(" • ") : "Location not set",
    posterType: raw.poster_type || "Student",
    genderPreference: raw.gender_preference || "Any",
    institutePreference: raw.InstitutePreference?.name || "Any",
    weekdays: raw.Weekdays?.map((w) => w.name).filter(Boolean) || [],
    salaryText: buildSalaryText(raw),

    phone: raw.phone_number || "",
    whatsapp: raw.whatsapp_number || "",

    raw,
  };
}

export default function TuitionProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);

  const [tuition, setTuition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isTutor = user?.role === "tutor";

  const isLoggedIn = !!user;

  const [tutorData, setTutorData] = useState(null);
  const [visibleContact, setVisibleContact] = useState(false);
  const [applicationStarted, setApplicationStarted] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);

  const tutorId = tutorData?.id || tutorData?._id;

  const tutorProfileUrl = tutorId
    ? `${window.location.origin}/tutor/${tutorId}`
    : "";

  const currentPagePath = `${location.pathname}${location.search}`;

  const goToTutorSubmissionWithRedirect = () => {
    window.scrollTo(0, 0);

    navigate(
      `/tutor-submission?redirect=${encodeURIComponent(currentPagePath)}`,
    );
  };

  const getTutorToken = () =>
    user?.token ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token");

  const authHeaders = () => {
    const token = getTutorToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleApplyNow = () => {
    if (!tuition?.id) return;

    if (!isLoggedIn) {
      goToTutorSubmissionWithRedirect();
      return;
    }

    if (!isTutor) {
      goToTutorSubmissionWithRedirect();
      return;
    }

    if (visibleContact) {
      setVisibleContact(false);
      return;
    }

    setAgreementOpen(true);
  };

  const closeAgreement = () => {
    if (applyLoading) return;
    setAgreementOpen(false);
  };

  const continueApplyAfterAgreement = async () => {
    if (!tuition?.id) return;

    setApplyLoading(true);

    try {
      const token = getTutorToken();
      if (!token) {
        alert("Please login first.");
        navigate("/login");
        return;
      }

      const res = await fetch(
        `${API_BASE}/tutors/tuition-posts/${tuition.id}/reveal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ method: "phone" }),
        },
      );

      const data = await res.json().catch(() => ({}));

      if (data?.requiresPayment || data?.requiresPurchase) {
        localStorage.setItem("pending_unlock_post", String(tuition.id));
        setAgreementOpen(false);
        navigate("/packages");
        window.scrollTo(0, 0);
        return;
      }

      if (!res.ok || data?.success === false) {
        alert(data?.message || "Failed to unlock contact.");
        return;
      }

      const contact = data?.contact || {
        phone: data?.phone_number,
        whatsapp: data?.whatsapp_number,
      };

      setTuition((prev) => ({
        ...prev,
        phone: contact.phone || prev.phone,
        whatsapp: contact.whatsapp || prev.whatsapp,
      }));

      setVisibleContact(true);
      setApplicationStarted(true);
      setAgreementOpen(false);
    } catch (err) {
      console.error("Apply from tuition profile failed:", err);
      alert("Server error. Please try again.");
    } finally {
      setApplyLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    async function fetchTuition() {
      setLoading(true);
      setError("");

      try {
        /*
          Best backend endpoint:
          GET /api/students/tuition-posts/:id

          Fallback:
          If this endpoint does not exist yet, this code fetches the board
          and finds the tuition by id.
        */

        let rawPost = null;

        const singleRes = await fetch(
          `${API_BASE}/students/tuition-posts/${id}`,
        );

        if (singleRes.ok) {
          const singleData = await singleRes.json().catch(() => ({}));
          rawPost =
            singleData?.data ||
            singleData?.post ||
            singleData?.tuition ||
            singleData;
        } else {
          const boardRes = await fetch(
            `${API_BASE}/students/tuition-board?limit=500`,
          );
          const boardData = await boardRes.json();

          if (!boardRes.ok) {
            throw new Error(
              boardData?.message || "Unable to load tuition details.",
            );
          }

          rawPost = (boardData?.data || []).find(
            (item) => String(item.id) === String(id),
          );
        }

        if (!rawPost) {
          throw new Error("Tuition not found.");
        }

        if (mounted) {
          setTuition(mapTuition(rawPost));
        }
      } catch (err) {
        console.error("Fetch tuition profile error:", err);
        if (mounted) {
          setError(err.message || "Unable to load tuition details.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchTuition();

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!isTutor) return;

    async function fetchTutorProfile() {
      try {
        const res = await fetch(`${API_BASE}/tutors/me`, {
          headers: {
            ...authHeaders(),
          },
        });

        const data = await res.json().catch(() => ({}));

        if (res.ok) {
          setTutorData(data?.tutor || null);
        }
      } catch (err) {
        console.error("fetchTutorProfile error", err);
      }
    }

    fetchTutorProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutor]);

  const pageTitle = useMemo(() => {
    if (!tuition) return "Tuition Details | Tutor Network";
    return `${tuition.studentName} | ${tuition.level} Tuition | Tutor Network`;
  }, [tuition]);

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-10">
        <p className="text-center font-semibold text-slate-600">
          Loading tuition details...
        </p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow">
          <button
            type="button"
            onClick={() => navigate("/tuition-board")}
            className="mb-4 inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 font-semibold text-slate-700"
          >
            <ArrowLeft size={18} />
            Back to Tuition Board
          </button>

          <p className="text-red-600 font-bold">{error}</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`Tuition details for ${tuition.level}, ${tuition.medium}, ${tuition.location}`}
        />
      </Helmet>

      <main className="min-h-screen bg-slate-50 px-4 py-8">
        <section className="mx-auto max-w-4xl">
          <button
            type="button"
            onClick={() => navigate("/tuition-board")}
            className="mb-5 inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-100"
          >
            <ArrowLeft size={18} />
            Back to Tuition Board
          </button>

          <article className="overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-200">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-6 text-white">
              <div className="mb-3 inline-flex rounded-full bg-white/20 px-3 py-1 text-sm font-bold">
                Tuition ID: {tuition.id}
              </div>

              <h1 className="text-2xl font-bold sm:text-3xl">
                {tuition.studentName}
              </h1>

              <p className="mt-2 font-semibold text-emerald-50">
                Level: {tuition.level}
              </p>
              <p className="mt-2 font-semibold text-emerald-50">
                Medium: {tuition.medium}
              </p>
            </div>

            <div className="border-t border-slate-200 p-6">
              <h2 className="mb-3 text-lg font-bold text-slate-900">
                Subjects
              </h2>

              {tuition.subjects.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tuition.subjects.map((subject) => (
                    <span
                      key={subject}
                      className="rounded-xl bg-blue-50 px-3 py-1.5 text-sm font-bold text-blue-700 ring-1 ring-blue-100"
                    >
                      {subject}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No subject specified.</p>
              )}
            </div>

            <div className="grid gap-4 p-6 sm:grid-cols-2">
              <InfoCard
                icon={<MapPin size={20} />}
                label="Location"
                value={tuition.location}
              />

              <InfoCard
                icon={<Banknote size={20} />}
                label="Salary"
                value={tuition.salaryText}
              />

              <InfoCard
                icon={<User size={20} />}
                label="Preferred Gender"
                value={
                  tuition.genderPreference === "Any"
                    ? "Any Gender"
                    : `${tuition.genderPreference} tutor`
                }
              />

              <InfoCard
                icon={<Building size={20} />}
                label="Preferred Background"
                value={tuition.institutePreference}
              />

              <InfoCard
                icon={<CalendarDays size={20} />}
                label="Weekdays"
                value={
                  tuition.weekdays.length > 0
                    ? tuition.weekdays.join(", ")
                    : "Not specified"
                }
              />

              <InfoCard
                icon={<BookOpen size={20} />}
                label="Posted By"
                value={tuition.posterType}
              />
            </div>

            <div className="border-t border-slate-200 bg-slate-50 p-6">
              <div className="border-t border-slate-200 bg-slate-50 p-6">
                {!isLoggedIn ? (
                  <button
                    type="button"
                    onClick={goToTutorSubmissionWithRedirect}
                    className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-5 py-3 font-extrabold text-white shadow-lg shadow-emerald-500/25 hover:scale-[1.01]"
                  >
                    Join as Tutor to Apply
                  </button>
                ) : isTutor ? (
                  <>
                    <button
                      type="button"
                      onClick={handleApplyNow}
                      disabled={applyLoading}
                      className={`w-full rounded-xl px-5 py-3 font-extrabold shadow-lg transition-all duration-300 hover:scale-[1.01] ${
                        visibleContact
                          ? "bg-slate-200 text-slate-800 shadow-slate-400/20 hover:bg-slate-300"
                          : "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-emerald-500/25"
                      }`}
                    >
                      {applyLoading
                        ? "Please wait..."
                        : visibleContact
                          ? "Hide Contact"
                          : "Apply Now"}
                    </button>

                    {visibleContact && (
                      <div className="mt-4 space-y-4 rounded-xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-cyan-50 p-4">
                        <div>
                          <p className="text-sm font-extrabold text-slate-900">
                            Your Application Process:
                          </p>

                          <div className="mt-3 space-y-2 text-sm font-bold">
                            <div className="flex items-center gap-2 text-emerald-700">
                              ✓ Step 1: Guardian-এর সাথে সরাসরি কথা বলুন
                            </div>

                            <div className="flex items-center gap-2 text-slate-700">
                              ○ Step 2: নিজের Profile পাঠান
                            </div>
                          </div>

                          <p className="mt-3 text-xs font-semibold text-slate-600">
                            Profile পাঠালে Guardian আপনার তথ্য সহজে মনে রাখতে
                            পারবেন এবং আপনাকে বিবেচনা করার সম্ভাবনা বাড়বে।
                          </p>
                        </div>

                        {tuition.phone && (
                          <a
                            href={buildTutorCallLink(tuition.phone)}
                            className="flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-3 font-extrabold text-white shadow-lg"
                          >
                            Call Guardian
                          </a>
                        )}

                        {tuition.whatsapp
                          ? (() => {
                              const waPhone = normalizeBDPhone(
                                tuition.whatsapp,
                              );

                              const postWhatsappText = buildTutorWhatsappText({
                                tutor: tutorData,
                                tutorProfileUrl,
                                tuitionCode: tuition.studentName,
                              });

                              return waPhone ? (
                                <a
                                  href={`https://api.whatsapp.com/send?phone=${waPhone}&text=${encodeURIComponent(
                                    postWhatsappText,
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center rounded-xl bg-gradient-to-r from-green-600 to-green-500 px-4 py-3 font-extrabold text-white shadow-lg"
                                >
                                  Send CV to Guardian
                                </a>
                              ) : null;
                            })()
                          : null}
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={goToTutorSubmissionWithRedirect}
                    className="w-full rounded-xl bg-slate-200 px-5 py-3 font-bold text-slate-800 hover:bg-slate-300"
                  >
                    Only tutors can apply — Register as a tutor
                  </button>
                )}
              </div>
            </div>
          </article>
        </section>
      </main>

      {agreementOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 sm:items-center"
          onClick={closeAgreement}
        >
          <div
            className="
        flex w-full max-w-lg flex-col
        rounded-t-3xl bg-white shadow-2xl
        sm:rounded-2xl
        max-h-[90vh]
      "
            onClick={(event) => event.stopPropagation()}
          >
            {/* Header */}
            <div className="border-b border-slate-100 px-5 pb-4 pt-6 sm:p-6">
              <h2 className="text-lg font-black text-slate-950 sm:text-xl">
                Tutor Application Agreement
              </h2>

              <p className="mt-2 text-sm font-medium leading-6 text-slate-500">
                Before applying, please read and agree to the following terms.
              </p>
            </div>

            {/* Scroll Content */}
            <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="space-y-4">
                <div
                  className="
            rounded-2xl border border-amber-200 
            bg-amber-50 p-4
          "
                >
                  <p
                    className="
              text-sm font-bold leading-6 text-amber-800
            "
                  >
                    1. I understand that I am required to provide 2 demo classes
                    to the guardian for final confirmation.
                  </p>
                </div>

                <div
                  className="
            rounded-2xl border border-blue-200 
            bg-blue-50 p-4
          "
                >
                  <p
                    className="
              text-sm font-bold leading-6 text-blue-800
            "
                  >
                    2. Tutor Network checks tuition confirmation with the
                    guardian. If my tuition is confirmed, 50% of the first
                    month’s salary must be paid within 7 days of confirmation.
                    This is a one-time payment.
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile Sticky Actions */}
            <div
              className="
        border-t border-slate-100
        bg-white
        px-5 py-4
        sm:px-6
      "
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={applyLoading}
                  onClick={closeAgreement}
                  className="
              min-h-[48px]
              rounded-xl
              border border-slate-200
              bg-white
              px-4 py-3
              text-sm font-black
              text-slate-700
              transition
              hover:bg-slate-50
              disabled:opacity-50
            "
                >
                  Disagree
                </button>

                <button
                  type="button"
                  disabled={applyLoading}
                  onClick={continueApplyAfterAgreement}
                  className="
              min-h-[48px]
              rounded-xl
              bg-gradient-to-r
              from-emerald-600
              to-emerald-500
              px-4 py-3
              text-sm font-black
              text-white
              shadow-lg
              shadow-emerald-500/25
              transition
              active:scale-[0.98]
              disabled:opacity-50
            "
                >
                  {applyLoading ? "Please wait..." : "Agree & Continue"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center gap-2 text-emerald-600">
        {icon}
        <span className="text-sm font-bold uppercase tracking-wide">
          {label}
        </span>
      </div>

      <p className="font-extrabold text-slate-900">
        {value || "Not specified"}
      </p>
    </div>
  );
}
